import { useEffect, useRef } from 'react';
import Box from '@mui/material/Box';

/** Gold, orange, pink and purple bursts to match the Dev Deepawali sky. */
const BURST_COLORS = [
  [255, 210, 110],
  [255, 170, 60],
  [255, 120, 190],
  [190, 120, 255],
] as const;

type Mode = 'full' | 'lite';

interface Rocket {
  x: number;
  y: number;
  vy: number;
  targetY: number;
  color: (typeof BURST_COLORS)[number];
}

interface Spark {
  x: number;
  y: number;
  px: number;
  py: number;
  vx: number;
  vy: number;
  life: number;
  decay: number;
  color: (typeof BURST_COLORS)[number];
}

const SETTINGS: Record<Mode, { sparks: number; minDelay: number; maxDelay: number; maxDpr: number }> = {
  full: { sparks: 70, minDelay: 1100, maxDelay: 2800, maxDpr: 2 },
  lite: { sparks: 36, minDelay: 2600, maxDelay: 5200, maxDpr: 1 },
};

const GRAVITY = 0.035;
const FRICTION = 0.985;

/**
 * Returns null when the animation should not run at all (reduced motion,
 * data saver, very weak devices); 'lite' for phones and modest hardware.
 */
function resolveMode(): Mode | null {
  if (typeof window === 'undefined') return null;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return null;

  const nav = navigator as Navigator & {
    connection?: { saveData?: boolean };
    deviceMemory?: number;
  };
  if (nav.connection?.saveData) return null;

  const cores = nav.hardwareConcurrency ?? 4;
  const memory = nav.deviceMemory ?? 4;
  if (cores <= 2 || memory <= 1) return null;

  const isSmallScreen = window.matchMedia('(max-width: 899.95px)').matches;
  return isSmallScreen || cores <= 4 || memory <= 2 ? 'lite' : 'full';
}

const rand = (min: number, max: number) => min + Math.random() * (max - min);

export function HeroFireworks() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;

    const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    let mode = resolveMode();
    let width = 0;
    let height = 0;
    let rockets: Rocket[] = [];
    let sparks: Spark[] = [];
    let frameId: number | null = null;
    let launchTimer: number | null = null;
    let inView = true;

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, SETTINGS[mode ?? 'lite'].maxDpr);
      width = rect.width;
      height = rect.height;
      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const explode = (rocket: Rocket) => {
      const count = SETTINGS[mode ?? 'lite'].sparks;
      const power = rand(2.2, 3.4);
      for (let i = 0; i < count; i++) {
        const angle = (Math.PI * 2 * i) / count + rand(-0.08, 0.08);
        const speed = power * rand(0.55, 1);
        sparks.push({
          x: rocket.x,
          y: rocket.y,
          px: rocket.x,
          py: rocket.y,
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed,
          life: 1,
          decay: rand(0.011, 0.018),
          // A few sparks pick up a second hue for a richer burst.
          color: Math.random() < 0.18 ? BURST_COLORS[0] : rocket.color,
        });
      }
    };

    const step = () => {
      frameId = null;
      ctx.clearRect(0, 0, width, height);
      ctx.globalCompositeOperation = 'lighter';
      ctx.lineCap = 'round';

      rockets = rockets.filter((r) => {
        const prevY = r.y;
        r.y += r.vy;
        r.vy *= 0.99;
        ctx.strokeStyle = 'rgba(255, 220, 150, 0.85)';
        ctx.lineWidth = 1.6;
        ctx.beginPath();
        ctx.moveTo(r.x, prevY + 10);
        ctx.lineTo(r.x, r.y);
        ctx.stroke();
        if (r.y <= r.targetY) {
          explode(r);
          return false;
        }
        return true;
      });

      sparks = sparks.filter((s) => {
        s.px = s.x;
        s.py = s.y;
        s.vx *= FRICTION;
        s.vy = s.vy * FRICTION + GRAVITY;
        s.x += s.vx;
        s.y += s.vy;
        s.life -= s.decay;
        if (s.life <= 0) return false;
        const [r, g, b] = s.color;
        ctx.strokeStyle = `rgba(${r}, ${g}, ${b}, ${s.life * 0.9})`;
        ctx.lineWidth = 1.4 + s.life;
        ctx.beginPath();
        ctx.moveTo(s.px, s.py);
        ctx.lineTo(s.x, s.y);
        ctx.stroke();
        return true;
      });

      ctx.globalCompositeOperation = 'source-over';
      // Only keep painting while something is in the sky.
      if (rockets.length || sparks.length) frameId = requestAnimationFrame(step);
    };

    const ensureLoop = () => {
      if (frameId === null) frameId = requestAnimationFrame(step);
    };

    const launch = () => {
      if (!width || !height) return;
      // Bias launches away from the far-left copy column on wide screens.
      const minX = width >= 900 ? 0.3 : 0.1;
      rockets.push({
        x: width * rand(minX, 0.95),
        y: height,
        vy: -rand(5.5, 7.5),
        targetY: height * rand(0.12, 0.45),
        color: BURST_COLORS[Math.floor(Math.random() * BURST_COLORS.length)],
      });
      ensureLoop();
    };

    const stopLaunching = () => {
      if (launchTimer !== null) window.clearTimeout(launchTimer);
      launchTimer = null;
    };

    const scheduleLaunch = (delay?: number) => {
      stopLaunching();
      if (!mode || !inView || document.hidden) return;
      const { minDelay, maxDelay } = SETTINGS[mode];
      launchTimer = window.setTimeout(() => {
        launch();
        scheduleLaunch();
      }, delay ?? rand(minDelay, maxDelay));
    };

    const clearSky = () => {
      rockets = [];
      sparks = [];
      if (frameId !== null) cancelAnimationFrame(frameId);
      frameId = null;
      ctx.clearRect(0, 0, width, height);
    };

    const handleVisibility = () => {
      if (document.hidden) {
        stopLaunching();
        clearSky();
      } else {
        scheduleLaunch();
      }
    };

    const handleMotionChange = () => {
      mode = resolveMode();
      if (!mode) {
        stopLaunching();
        clearSky();
      } else {
        scheduleLaunch();
      }
    };

    resize();
    const resizeObserver = new ResizeObserver(resize);
    resizeObserver.observe(canvas);

    const intersectionObserver = new IntersectionObserver(([entry]) => {
      inView = entry.isIntersecting;
      if (inView) {
        if (launchTimer === null) scheduleLaunch();
      } else {
        stopLaunching();
        clearSky();
      }
    });
    intersectionObserver.observe(canvas);

    document.addEventListener('visibilitychange', handleVisibility);
    motionQuery.addEventListener('change', handleMotionChange);

    scheduleLaunch(600);

    return () => {
      stopLaunching();
      clearSky();
      resizeObserver.disconnect();
      intersectionObserver.disconnect();
      document.removeEventListener('visibilitychange', handleVisibility);
      motionQuery.removeEventListener('change', handleMotionChange);
    };
  }, []);

  return (
    <Box
      component="canvas"
      ref={canvasRef}
      aria-hidden="true"
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: { xs: '45%', md: '62%' },
        pointerEvents: 'none',
        // Bursts fade out before they reach the ghats and the search bar.
        maskImage: 'linear-gradient(to bottom, #000 0%, #000 60%, transparent 100%)',
        WebkitMaskImage: 'linear-gradient(to bottom, #000 0%, #000 60%, transparent 100%)',
      }}
    />
  );
}
